import React, { useMemo, useState } from "react";
import type { ComponentProps } from "react";
import { GaraazPlaceEnquiryForm } from "@svan-garaaz/reactsharedlib";
import Services from "./services";
import useNotification from "../../utils/useNotification";
import globalConstants from "../../utils/constants";
import PageScaffold from "../../components/PageScaffold";
import { useGetOrderMediaConfig } from "./queryHooks";
import "./PlaceOrderFormTest.css";

type GaraazPlaceEnquiryFormValues = Parameters<
  NonNullable<ComponentProps<typeof GaraazPlaceEnquiryForm>["onSubmit"]>
>[0];

/** Hide challan/extra columns Orca does not use on place enquiry. */
const PLACE_ENQUIRY_FIELDS: NonNullable<
  ComponentProps<typeof GaraazPlaceEnquiryForm>["fields"]
> = {
  workshopCode: { isVisible: false },
  associatedBranchName: { isVisible: false },
  mrp: { isVisible: false },
  dateOfPurchase: { isVisible: false },
  totalAmount: { isVisible: false },
};

const PlaceOrderFormTest: React.FC = () => {
  const [formKey, setFormKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useNotification();
  const authToken = localStorage.getItem(globalConstants.AUTH_TOKEN) ?? "";
  const { data: mobileConfig } = useGetOrderMediaConfig();

  const typesenseConfig = useMemo(() => {
    const rawHost = mobileConfig?.typesense_host?.trim();
    const apiKey = mobileConfig?.typesense_api_key?.trim();
    if (!rawHost || !apiKey) return null;
    const host = rawHost.replace(/^https?:\/\//i, "").split("/")[0];
    if (!host) return null;
    return { host, apiKey };
  }, [mobileConfig?.typesense_host, mobileConfig?.typesense_api_key]);

  const handleSubmit = async (values: GaraazPlaceEnquiryFormValues) => {
    // --- Validation (mirrors existing enablePlaceOrder logic) ---
    if (!values.selectedCustomer) {
      toast({ description: "Please select a customer.", status: "error" });
      return;
    }
    if (!values.customerAddress) {
      toast({
        description: "Please select a delivery address.",
        status: "error",
      });
      return;
    }
    if (!values.orderDateTime) {
      toast({
        description: "Please set an order date and time.",
        status: "error",
      });
      return;
    }
    if (!values.carMaker) {
      toast({ description: "Please select a car maker.", status: "error" });
      return;
    }
    const hasContent =
      (values.remarks ?? "").trim().length > 0 ||
      values.media.length > 0 ||
      values.partsTableData.length > 0;
    if (!hasContent) {
      toast({
        description: "Please add at least one part, media, or a remark.",
        status: "error",
      });
      return;
    }
    const hasZeroQty = values.partsTableData.some(
      (p) => p.orderedQuantity <= 0,
    );
    if (hasZeroQty) {
      toast({
        description: "All parts must have a quantity greater than zero.",
        status: "error",
      });
      return;
    }

    // --- Submit ---
    setIsSubmitting(true);
    try {
      const user = JSON.parse(
        localStorage.getItem(globalConstants.ORCA_USER) ?? "{}",
      );
      const {
        insuranceClaimId,
        spocDetails,
        surveyorDetails,
        ...restBilling
      } = values.billingDetails ?? {};
      const payload = {
        customerId: values.selectedCustomer._id,
        initiatorId: user._id,
        customerRemarks: values.remarks,
        shipmentAddress: values.customerAddress,
        orderDate: new Date(values.orderDateTime),
        orderItems: values.partsTableData.map((p) => ({
          partId: p._id,
          orderedQuantity: p.orderedQuantity,
        })),
        orderMedia:
          values.media.length > 0
            ? values.media.map((m) => m.mediaId)
            : undefined,
        carMaker: values.carMaker || undefined,
        licensePlateNumber: values.licensePlateNumber || undefined,
        carModel: values.carModel || undefined,
        vinNumber: values.vinNumber || undefined,
        billingDetails:
          Object.keys(restBilling).length > 0 ? restBilling : undefined,
        insuranceClaimId: insuranceClaimId || undefined,
        ...(spocDetails &&
        (spocDetails.name?.trim() ||
          spocDetails.mobileNumber?.trim() ||
          spocDetails.estimateApprovalFormUrl?.trim())
          ? { spocDetails }
          : {}),
        ...(surveyorDetails &&
        (surveyorDetails.name?.trim() || surveyorDetails.mobileNumber?.trim())
          ? { surveyorDetails }
          : {}),
      };
      const order = await Services.placeGaraazOrder(payload);
      if (order) {
        toast({
          description: `Thank you! Your enquiry (${order.orderNo}) has been placed and is being processed.`,
          status: "success",
        });
        setFormKey((k) => k + 1);
      } else {
        toast({
          description: "We're unable to place your enquiry. Please try again.",
          status: "error",
        });
      }
    } catch {
      toast({
        description: "We're unable to place your enquiry. Please try again.",
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageScaffold title="Place Enquiry" isHeaderVisible={false}>
      <div className="orca-place-enquiry-form">
        <GaraazPlaceEnquiryForm
          key={formKey}
          authToken={authToken}
          apiBaseUrl={process.env.REACT_APP_BASE_URL ?? ""}
          typesenseConfig={typesenseConfig}
          addCountryCodeToSearch
          fields={PLACE_ENQUIRY_FIELDS}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onNotification={toast}
        />
      </div>
    </PageScaffold>
  );
};

export default PlaceOrderFormTest;
